import React, { useEffect, useState } from 'react';
import { Template } from '../../../interfaces/backend/Template';
import confirm from 'antd/lib/modal/confirm';
import { Dropdown } from 'antd';
import { EllipsisOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { TemplatesPATCHDefaultRequest } from '../../../interfaces/backend/TemplatesPATCHDefaultRequest';
import { Channels } from '../../common/channelsEnum';
import { Notification } from '../../../interfaces/backend/Notifications';
import { ItemType } from 'antd/es/menu/hooks/useItems';

interface Props {
  template: Template;
  notification?: Notification;
  setTemplateBeingDuplicated: (template: Template) => void;
  makeDefault: (
    channel: string,
    templateId: string,
    modes?: TemplatesPATCHDefaultRequest['modes']
  ) => void;
  deleteTemplate: (channel: string, templateId: string) => void;
}

const TemplateCardContextMenu = (props: Props): JSX.Element => {
  const [deliveryOptionItems, setDeliveryOptionItems] = useState<ItemType[]>(
    []
  );
  useState(false);
  useEffect(() => {
    const modes: TemplatesPATCHDefaultRequest['modes'] = [
      'instant',
      'hourly',
      'daily',
      'weekly',
      'monthly'
    ];
    if (props.notification && props.notification.options) {
      const emailDeliveryOptions = props.notification.options['EMAIL'];
      setDeliveryOptionItems(
        modes
          // filter by notification
          .filter((item) => {
            return (
              emailDeliveryOptions as {
                [key: string]: { enabled: boolean };
              }
            )[item].enabled;
          })
          // filter by template
          .filter((item) => {
            return !(
              props.template.isDefaultFor as {
                [key: string]: boolean;
              }
            )[item];
          })
          .map((item): ItemType => {
            return {
              label: `Set as default ${item} delivery option`,
              key: `promote to ${item}`,
              danger: false,

              onClick: (e) => {
                e.domEvent.stopPropagation();
                confirm({
                  title: 'Are you sure?',
                  icon: <ExclamationCircleFilled />,
                  okType: 'danger',
                  okText: 'Continue',
                  cancelText: 'No',
                  content: `If you continue, this template will be set as default ${item} delivery template for all your ${props.template.notificationId} notifications by default (except when templateId is specified explicitly in the code).`,
                  onOk() {
                    return props.makeDefault(
                      props.template.channel,
                      props.template.templateId,
                      [item]
                    );
                  }
                });
              }
            };
          })
      );
    }
  }, [setDeliveryOptionItems, props]);

  return (
    <Dropdown
      menu={{
        items: [
          {
            label: `Copy Template ID (${props.template.templateId})`,
            key: 'copyTemplateId',
            danger: false,
            onClick: (e) => {
              e.domEvent.stopPropagation();
              navigator.clipboard.writeText(props.template.templateId);
            }
          },
          {
            label: 'Duplicate Template',
            key: 'duplicate',
            danger: false,
            onClick: (e) => {
              e.domEvent.stopPropagation();
              props.setTemplateBeingDuplicated(props.template);
            }
          },
          ...(props.template.channel === Channels.EMAIL
            ? deliveryOptionItems
            : [
                {
                  label: props.template.default ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {'Promote to Default (Already default)'}
                    </div>
                  ) : (
                    'Promote to Default'
                  ),
                  key: 'promote',
                  danger: false,
                  disabled: props.template.default,
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    confirm({
                      title: 'Are you sure?',
                      icon: <ExclamationCircleFilled />,
                      okType: 'danger',
                      okText: 'Continue',
                      cancelText: 'No',
                      content: `If you continue, this template will be used for all your ${props.template.notificationId} notifications by default (except when templateId is specified explicitly in the code).`,
                      onOk() {
                        return props.makeDefault(
                          props.template.channel,
                          props.template.templateId
                        );
                      }
                    });
                  }
                } as ItemType
              ]),
          {
            label: props.template.default ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {"Delete (Can't delete default)"}
              </div>
            ) : (
              'Delete'
            ),

            key: 'delete',
            danger: true,
            disabled: props.template.default,
            onClick: (e) => {
              e.domEvent.stopPropagation();
              confirm({
                title: 'Are you sure?',
                icon: <ExclamationCircleFilled />,
                okType: 'danger',
                okText: 'Yes',
                cancelText: 'No',
                content:
                  'If you used this template in your code, after deletion, your notifications will route through the default template.',
                onOk() {
                  return props.deleteTemplate(
                    props.template.channel,
                    props.template.templateId
                  );
                }
              });
            }
          }
        ]
      }}
      placement="bottom"
      trigger={['click']}
    >
      <EllipsisOutlined
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </Dropdown>
  );
};

export default TemplateCardContextMenu;
